/**
 * Shared design tokens — International Typographic Style (Swiss design):
 * one grotesque typeface, a strict 8pt grid, hairline rules instead of
 * shadows, and black/white/one accent (Dutch orange) as the entire palette.
 */
export const grid = 8;
export const spacing = {
  xxs: grid * 0.5, // 4
  xs: grid, // 8
  sm: grid * 1.5, // 12
  md: grid * 2, // 16
  lg: grid * 3, // 24
  xl: grid * 4, // 32
  xxl: grid * 6, // 48
  xxxl: grid * 8, // 64
} as const;

export const borderWidths = { none: 0, hairline: 1, rule: 2 } as const;
/** Swiss layout is flat — square corners everywhere except pills/dots. */
export const radii = { none: 0, small: 2, pill: 9999 } as const;
export const shadows = {
  none: {
    shadowColor: "transparent",
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
} as const;

// A tight, modular type scale (each step ~1.25x) read on one grotesque
// family. The counter figure itself is set far larger, in its own
// component, using the same family with tabular numerals.
export const typography = {
  caption: 12,
  label: 14,
  body: 16,
  heading3: 20,
  heading2: 26,
  heading1: 34,
  display: 30,
} as const;

export const fontWeights = {
  regular: "400" as const,
  medium: "500" as const,
  semibold: "600" as const,
  bold: "700" as const,
};

// Inter — a neutral grotesque built for screens, the closest widely
// available substitute for Helvetica in this typographic tradition. One
// family throughout; numerals stay in-family with tabular figures rather
// than switching to a mono, per the brief.
export const fontFamilies = {
  regular: "Inter_400Regular",
  medium: "Inter_500Medium",
  semibold: "Inter_600SemiBold",
  bold: "Inter_700Bold",
} as const;

export const control = {
  height: 48,
  heightCompact: 36,
} as const;

export const layout = {
  pageMaxWidth: 960,
  modalMaxWidth: 480,
} as const;

// Black / white / one accent — the classic Swiss poster palette, with a
// Dutch orange standing in for the usual Swiss red.
export const lightColors = {
  ink: "#0a0a0a",
  background: "#ffffff",
  surface: "#ffffff",
  surfaceMuted: "#f2f2f2",
  rule: "rgba(10, 10, 10, 0.14)",
  ruleStrong: "rgba(10, 10, 10, 0.85)",
  muted: "rgba(10, 10, 10, 0.55)",
  placeholder: "rgba(10, 10, 10, 0.40)",
  inverse: "#ffffff",
  overlay: "rgba(10, 10, 10, 0.55)",
  accent: "#e8600a",
  accentInverse: "#ffffff",
  danger: "#e8600a",
  dangerBackground: "#fbe6d8",
} as const;

export type ThemeColors = { [Key in keyof typeof lightColors]: string };

export const darkColors: ThemeColors = {
  ink: "#f5f5f5",
  background: "#0a0a0a",
  surface: "#0a0a0a",
  surfaceMuted: "#171717",
  rule: "rgba(245, 245, 245, 0.16)",
  ruleStrong: "rgba(245, 245, 245, 0.85)",
  muted: "rgba(245, 245, 245, 0.58)",
  placeholder: "rgba(245, 245, 245, 0.40)",
  inverse: "#0a0a0a",
  overlay: "rgba(0, 0, 0, 0.70)",
  accent: "#ff9142",
  accentInverse: "#0a0a0a",
  danger: "#ff9142",
  dangerBackground: "#3a2413",
} as const;

export const counterColorValues: Record<
  "ink" | "red",
  { light: string; dark: string }
> = {
  ink: { light: lightColors.ink, dark: darkColors.ink },
  red: { light: lightColors.accent, dark: darkColors.accent },
};
